import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../../i18n';
import { api } from '../../services/api';
import { useAuth } from './AuthContext';
import useModalEscape from './useModalEscape';

const PENDING_OTP_KEY = 'od_pending_otp';

function readPendingOtp() {
  try {
    const pending = JSON.parse(localStorage.getItem(PENDING_OTP_KEY) || 'null');
    if (!pending?.email || !pending?.expiresAt || Number(pending.expiresAt) <= Date.now()) {
      localStorage.removeItem(PENDING_OTP_KEY);
      return null;
    }
    return pending;
  } catch {
    localStorage.removeItem(PENDING_OTP_KEY);
    return null;
  }
}

export function AuthPromptPanel({
  title = '',
  copy = '',
  onNavigate = null,
  onLegalOpen = null,
  className = '',
}) {
  const { completeLogin } = useAuth();
  const { language, t } = useI18n();
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('email');
  const [pendingOtp, setPendingOtp] = useState(null);
  const [devOtp, setDevOtp] = useState('');
  const [status, setStatus] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const pending = readPendingOtp();
    setPendingOtp(pending);
    setEmail(pending?.email || '');
    setOtp('');
    setDevOtp('');
    setStep(pending ? 'otp' : 'email');
    setStatus(pending ? t('login.pendingOtpRestored') : '');
  }, [t]);

  async function requestOtp(event, targetEmail = email) {
    event?.preventDefault();
    setSubmitting(true);
    setStatus('');

    try {
      const normalizedEmail = targetEmail.trim().toLowerCase();
      const res = await api.requestOtp(normalizedEmail, language);
      const minutes = Number(res.expires_in_minutes || 10);
      const pending = {
        email: normalizedEmail,
        expiresAt: Date.now() + minutes * 60 * 1000,
      };
      localStorage.setItem(PENDING_OTP_KEY, JSON.stringify(pending));
      setPendingOtp(pending);
      setEmail(normalizedEmail);
      setOtp('');
      setDevOtp(res.dev_otp || '');
      setStep('otp');
      setStatus(t('login.codeSent', { minutes }));
    } catch (err) {
      setStatus(`Errore: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  }

  async function verifyOtp(event) {
    event.preventDefault();
    setSubmitting(true);
    setStatus('');

    try {
      const res = await api.verifyOtp(email, otp);
      localStorage.removeItem(PENDING_OTP_KEY);
      await completeLogin(res.token, res.user, res.refresh_token, {
        isNewUser: res.is_new_user,
        followRefreshHint: res.follow_refresh_hint,
      });
    } catch (err) {
      setStatus(`Errore: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  }

  function changeEmail() {
    localStorage.removeItem(PENDING_OTP_KEY);
    setPendingOtp(null);
    setOtp('');
    setDevOtp('');
    setStatus('');
    setStep('email');
  }

  function openLegal(event, key) {
    if (!onLegalOpen) {
      onNavigate?.();
      return;
    }
    event.preventDefault();
    onNavigate?.();
    onLegalOpen(key);
  }

  return (
    <div className={`ct-auth-prompt-panel ${className}`.trim()}>
      <div className="ct-auth-prompt-head">
        <span><i className="ph ph-magic-wand"></i></span>
        <div>
          <h2 id="ct-auth-prompt-title">{title || t('authPrompt.title')}</h2>
          <p>{copy || t('authPrompt.copy')}</p>
        </div>
      </div>

      {step === 'email' ? (
        <form onSubmit={requestOtp} className="ct-auth-form">
          <label className="ct-auth-field">
            <input
              className="form-control"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder={t('login.emailPlaceholder')}
              aria-label={t('login.email')}
              required
            />
          </label>
          <button className="btn btn-dark" disabled={submitting}>
            {submitting ? t('login.sending') : t('authPrompt.sendCode')}
          </button>
          {pendingOtp ? (
            <button type="button" className="btn btn-link" onClick={() => setStep('otp')}>
              {t('login.useReceivedCode')}
            </button>
          ) : null}
        </form>
      ) : (
        <form onSubmit={verifyOtp} className="ct-auth-form">
          <label>
            {t('login.otp')}
            <input
              className="form-control ct-otp-input"
              value={otp}
              onChange={(event) => setOtp(event.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              inputMode="numeric"
              required
            />
          </label>
          {devOtp ? (
            <button type="button" className="ct-dev-otp" onClick={() => setOtp(devOtp)}>
              {t('login.useDevOtp', { otp: devOtp })}
            </button>
          ) : null}
          <button className="btn btn-dark" disabled={submitting || otp.length !== 6}>
            {submitting ? t('login.verifying') : t('authPrompt.verify')}
          </button>
          <button type="button" className="btn btn-link" onClick={() => requestOtp(null, email)} disabled={submitting}>
            {submitting ? t('login.sending') : t('login.resendCode')}
          </button>
          <button type="button" className="btn btn-link" onClick={changeEmail}>
            {t('login.changeEmail')}
          </button>
        </form>
      )}

      {status ? <p className="ct-success">{status}</p> : null}
      <p className="ct-login-legal">
        {t('login.legalPrefix')}{' '}
        <Link to="/privacy" onClick={(event) => openLegal(event, 'privacy')}>
          {t('login.legalLink')}
        </Link>{' '}
        {t('login.legalAnd')}{' '}
        <Link to="/terms" onClick={(event) => openLegal(event, 'terms')}>
          {t('login.termsLink')}
        </Link>.
      </p>
    </div>
  );
}

export default function AuthPromptDialog({ onLegalOpen = null }) {
  const { authPrompt, closeLoginPrompt, isAuthenticated, loginSuccessAt } = useAuth();
  const { t } = useI18n();
  const [showLoginToast, setShowLoginToast] = useState(false);
  useModalEscape(Boolean(authPrompt), closeLoginPrompt);

  useEffect(() => {
    if (!authPrompt || !isAuthenticated) return;
    closeLoginPrompt();
  }, [authPrompt, closeLoginPrompt, isAuthenticated]);

  useEffect(() => {
    if (!loginSuccessAt) return undefined;
    setShowLoginToast(true);
    const timer = window.setTimeout(() => setShowLoginToast(false), 2600);
    return () => window.clearTimeout(timer);
  }, [loginSuccessAt]);

  useEffect(() => {
    if (!authPrompt) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [authPrompt]);

  if (!authPrompt) {
    return showLoginToast ? <div className="ct-toast" role="status">{t('login.success')}</div> : null;
  }

  return (
    <>
      <div className="ct-auth-prompt-overlay" role="presentation" onMouseDown={closeLoginPrompt}>
        <section
          className="ct-auth-prompt-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ct-auth-prompt-title"
          onMouseDown={(event) => event.stopPropagation()}
        >
          <button type="button" className="ct-ai-close" onClick={closeLoginPrompt} aria-label={t('aiDialog.close')}>
            <i className="ph ph-x"></i>
          </button>
          <AuthPromptPanel
            title={authPrompt.title}
            copy={authPrompt.copy}
            onNavigate={closeLoginPrompt}
            onLegalOpen={onLegalOpen}
          />
        </section>
      </div>
      {showLoginToast ? <div className="ct-toast" role="status">{t('login.success')}</div> : null}
    </>
  );
}
